import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft, Loader2, Star, Trash2, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import PageMeta from "@/components/PageMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useWatchlist } from "@/hooks/useUserTokens";
import { useLivePrices } from "@/hooks/useLivePrices";
import { cn } from "@/lib/utils";

const fmtAud = (v?: number) => {
  if (v == null || !isFinite(v) || v === 0) return "—";
  if (v >= 1000) return `A$${v.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
  if (v >= 1) return `A$${v.toFixed(2)}`;
  return `A$${v.toFixed(v < 0.01 ? 6 : 4)}`;
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

/** Dedicated page listing the signed-in user's saved coins with live AUD prices. */
const Watchlist = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { items, isLoading, toggle } = useWatchlist();
  const [removing, setRemoving] = useState<string | null>(null);

  const symbols = items.map((i) => i.token_symbol.toUpperCase());
  const { prices } = useLivePrices(symbols);

  const remove = async (symbol: string) => {
    setRemoving(symbol);
    try {
      await toggle(symbol);
      toast.success(`${symbol} removed from your watchlist`);
    } catch {
      toast.error("Couldn't remove that coin — please try again.");
    } finally {
      setRemoving(null);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="My Watchlist | GoToCoins"
        description="Your saved coins with live AUD prices and 24-hour change, synced to your GoToCoins account."
        path="/watchlist"
      />

      <div className="container mx-auto px-4 py-8 max-w-3xl space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="space-y-1">
          <span className="type-eyebrow text-primary">Your coins</span>
          <h1 className="type-page-title flex items-center gap-2">
            <Star className="w-6 h-6 text-primary" /> My Watchlist
          </h1>
          <p className="text-sm text-muted-foreground">
            Saved to your account, so the same list shows on your phone and desktop. Prices in AUD,
            refreshed every minute.
          </p>
        </div>

        {!user ? (
          <Card>
            <CardContent className="p-8 text-center space-y-4">
              <p className="text-muted-foreground">
                Sign in to save coins and see them here on any device.
              </p>
              <Button asChild>
                <Link to="/auth">Sign in</Link>
              </Button>
            </CardContent>
          </Card>
        ) : isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center space-y-4">
              <p className="text-muted-foreground">
                Nothing saved yet — tap the star on any coin to add it here.
              </p>
              <Button asChild variant="outline">
                <Link to="/opportunities">Browse coins</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {items.map((item) => {
                const sym = item.token_symbol.toUpperCase();
                const live = prices.get(sym);
                const ch = live?.change24h;
                const up = (ch ?? 0) >= 0;
                return (
                  <div key={sym} className="flex items-center gap-3 p-4">
                    <Link
                      to={`/coin-reviewer?symbol=${sym}`}
                      className="flex-1 min-w-0 flex items-center gap-3 hover:opacity-80"
                    >
                      <div className="min-w-0">
                        <div className="type-data font-bold">{sym}</div>
                        <div className="text-xs text-muted-foreground">
                          Saved {fmtDate(item.added_at)}
                        </div>
                      </div>
                    </Link>

                    <div className="text-right">
                      <div className="type-data-sm font-semibold tabular-nums">
                        {fmtAud(live?.price)}
                      </div>
                      {ch == null ? (
                        <div className="text-xs text-muted-foreground">—</div>
                      ) : (
                        <div
                          className={cn(
                            "text-xs font-medium tabular-nums flex items-center justify-end gap-1",
                            up ? "text-success" : "text-destructive",
                          )}
                        >
                          {up ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : (
                            <TrendingDown className="w-3 h-3" />
                          )}
                          {up ? "+" : ""}
                          {ch.toFixed(2)}%
                        </div>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${sym} from watchlist`}
                      disabled={removing === sym}
                      onClick={() => remove(sym)}
                      className="text-muted-foreground hover:text-destructive shrink-0"
                    >
                      {removing === sym ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Watchlist;
